import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { validateUrlParameter } from 'utils/security';
import { useDispatch } from 'react-redux';
import { authLoadingHandler } from 'store/slices/authSlice';

const useRouteHandler = () => {
  /**
   * Checks if current route should skip authentication (automatically gets current path)
   * @param {Set<string>} skipAuthRoutes - Set of routes that should skip authentication
   * @returns {Object} - { isValid: boolean, shouldSkipAuth: boolean, sanitized: string }
   */

  const location = useLocation();
  const dispatch = useDispatch();
  const [shouldSkipAuth, setShouldSkipAuth] = useState(false);

  // Handle dispatch in useEffect to avoid race conditions during render
  useEffect(() => {
    if (shouldSkipAuth) {
      dispatch(authLoadingHandler(false));
    }
  }, [shouldSkipAuth, dispatch]);

  const shouldSkipAuthForRoute = useCallback(
    (
      skipAuthRoutes = new Set(['/reset-password', '/confirm-email', '/login'])
    ) => {
      // Use React Router's location for consistent state during navigation
      const currentPath = location.pathname;

      // Validate and normalize current path
      const pathValidation = validateUrlParameter(currentPath, skipAuthRoutes);

      const skipAuth =
        pathValidation.isValid && skipAuthRoutes.has(pathValidation.sanitized);

      setShouldSkipAuth(skipAuth);
      return {
        isValid: pathValidation.isValid,
        shouldSkipAuth: skipAuth,
        sanitized: pathValidation.sanitized,
      };
    },
    [location.pathname]
  );
  return {
    shouldSkipAuthForRoute,
  };
};

export default useRouteHandler;
